import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MomentumTradesContainer.css";

const API_URL = " https://quatdashboard.com:8084/api/activetrades/grouped";

function MomentumTradesContainer() {
  const [data, setData] = useState({});
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientSection, setClientSection] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setData(res.data || {});
    } catch (err) {
      console.error("❌ Error fetching active trades:", err);
      alert("Failed to load active trades!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleExpandClient = (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      setClientSection((prev) => ({ ...prev, [clientId]: "active" }));
    }
  };

  const filteredClients = Object.keys(data).filter((clientId) =>
    clientId.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getClientSummary = (trades) => {
    const activeTrades = trades.filter(
      (t) => t.status?.toLowerCase() === "active",
    );
    const exitedTrades = trades.filter(
      (t) => t.status?.toLowerCase() === "exited",
    );

    const activeLivePnL = activeTrades.reduce(
      (sum, t) => sum + (t.pnl || 0),
      0,
    );
    const exitedPnL = exitedTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);

    return {
      totalActive: activeTrades.length,
      totalExited: exitedTrades.length,
      activeLivePnL,
      exitedPnL,
      activeTrades,
      exitedTrades,
    };
  };

  return (
    <div className="active-trades-screen">
      <h2 className="page-title">📈 MOMENTUM TRADES</h2>

      <div className="toolbar">
        <input
          type="text"
          placeholder="🔍 Search client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button onClick={fetchData}>🔄 Refresh</button>
      </div>

      {loading ? (
        <div className="loader">Loading trade data...</div>
      ) : filteredClients.length === 0 ? (
        <div className="loader">No clients found.</div>
      ) : (
        <div className="client-container">
          {filteredClients.map((clientId) => {
            const trades = data[clientId];
            const compounding = trades[0]?.compounding ?? false;
            const summary = getClientSummary(trades);
            const activeSection = clientSection[clientId] || "active";

            return (
              <div key={clientId} className="client-card">
                {/* HEADER */}
                <div
                  className="client-summary"
                  onClick={() => toggleExpandClient(clientId)}
                >
                  <div className="client-id">
                    <h3>
                      {clientId}{" "}
                      <span
                        style={{
                          marginLeft: "10px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: compounding ? "green" : "red",
                        }}
                      >
                        {compounding
                          ? "Compounding Enabled"
                          : "Compounding Disabled"}
                      </span>
                    </h3>

                    <div className="trade-section-tabs">
                      <div
                        className={`tab-item ${
                          activeSection === "active" ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setClientSection((prev) => ({
                            ...prev,
                            [clientId]: "active",
                          }));
                        }}
                      >
                        🟢 Active ({summary.totalActive})
                      </div>
                      <div
                        className={`tab-item ${
                          activeSection === "exited" ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setClientSection((prev) => ({
                            ...prev,
                            [clientId]: "exited",
                          }));
                        }}
                      >
                        🔴 Exited ({summary.totalExited})
                      </div>
                    </div>
                  </div>

                  <div className="summary-details">
                    <div
                      className={
                        summary.activeLivePnL >= 0 ? "positive" : "light-red"
                      }
                    >
                      Live PnL: ₹{summary.activeLivePnL.toFixed(2)}
                    </div>
                    <div
                      className={
                        summary.exitedPnL >= 0 ? "positive" : "light-red"
                      }
                    >
                      Realized: ₹{summary.exitedPnL.toFixed(2)}
                    </div>
                    <button className="expand-btn">
                      {expandedClient === clientId ? "▲" : "▼"}
                    </button>
                  </div>
                </div>

                {/* EXPANDED SECTION */}
                {expandedClient === clientId && (
                  <div className="expanded-trades">
                    {activeSection === "active" && (
                      <>
                        <h4 className="section-title">🟢 Active Trades</h4>
                        {summary.activeTrades.length > 0 ? (
                          <table className="trade-table">
                            <thead>
                              <tr>
                                <th>Symbol</th>
                                <th>Qty</th>
                                <th>Entry</th>
                                <th>Target</th>
                                <th>Stoploss</th>
                                <th>Setup</th>
                                <th>PnL</th>
                                <th>Days</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summary.activeTrades.map((t, i) => (
                                <tr key={i}>
                                  <td>{t.symbol}</td>
                                  <td>{t.quantity}</td>
                                  <td>{t.entry_price}</td>
                                  <td>{t.target}</td>
                                  <td>{t.sl}</td>
                                  <td>{t.setup}</td>
                                  <td
                                    className={
                                      (t.pnl || 0) >= 0
                                        ? "positive"
                                        : "light-red"
                                    }
                                  >
                                    {t.pnl !== null && t.pnl !== undefined
                                      ? `₹${t.pnl.toFixed(2)}`
                                      : "—"}
                                  </td>
                                  <td>
                                    {t.days_passed}/{t.max_days}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="no-trades">No active trades.</p>
                        )}
                      </>
                    )}

                    {activeSection === "exited" && (
                      <>
                        <h4 className="section-title">🔴 Exited Trades</h4>
                        {summary.exitedTrades.length > 0 ? (
                          <table className="trade-table">
                            <thead>
                              <tr>
                                <th>Symbol</th>
                                <th>Qty</th>
                                <th>Entry</th>
                                <th>Exit Price</th>
                                <th>Exit Date</th>
                                <th>Charges</th>
                                <th>PnL</th>
                                <th>Setup</th>
                                <th>Days</th>
                              </tr>
                            </thead>

                            <tbody>
                              {summary.exitedTrades.map((t, i) => (
                                <tr key={i}>
                                  <td>{t.symbol}</td>
                                  <td>{t.quantity}</td>

                                  {/* Entry Price */}
                                  <td>{t.entry_price?.toFixed(2) ?? "—"}</td>

                                  {/* Exit Price */}
                                  <td>
                                    {t.exit_price != null
                                      ? t.exit_price.toFixed(2)
                                      : "—"}
                                  </td>

                                  {/* Exit Date */}
                                  <td>
                                    {t.exit_date
                                      ? new Date(t.exit_date)
                                          .toISOString()
                                          .split("T")[0]
                                      : "—"}
                                  </td>

                                  {/* Charges */}
                                  <td>
                                    {t.charges != null
                                      ? `₹${t.charges.toFixed(2)}`
                                      : "—"}
                                  </td>

                                  {/* PnL */}
                                  <td
                                    className={
                                      (t.pnl || 0) >= 0
                                        ? "positive"
                                        : "light-red"
                                    }
                                  >
                                    {t.pnl != null
                                      ? `₹${t.pnl.toFixed(2)}`
                                      : "—"}
                                  </td>

                                  <td>{t.setup}</td>

                                  {/* Days Passed */}
                                  <td>
                                    {t.days_passed}/{t.max_days}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="no-trades">No exited trades.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MomentumTradesContainer;
